import { AspectRatio, Box, Fade, Flex, Image, Spacer, Text } from '@chakra-ui/react';
import useSetArticleReaction from 'hooks/modules/articles/useSetArticleReaction';
import useAppTheme from 'hooks/shared/useAppTheme';
import moment from 'moment';
import AppCard from 'presentation/components/shared/card/AppCard';
import ReactionsRow from 'presentation/components/shared/ReactionsRow';
import TagsRow from 'presentation/components/shared/TagsRow';
import { Color } from 'presentation/theme/colorScheme';
import { FC, useState } from 'react';
import bubbleIcon from 'assets/img/icons/message-bubble.svg';
import CommentAuthorsPreview from 'presentation/components/modules/comments/CommentAuthorsPreview';
import getCountText from 'utils/getCountText';
import { useTranslation } from 'react-i18next';
import chevronForward from 'assets/img/icons/chevron-forward.svg';
import { useNavigate } from 'react-router-dom';
import AppRoute from 'router/AppRoute';
import { availableReactions } from 'rtkQuery/sharespro/mapper/shared/mapReactionListItemFromApi';
import PollComponent from './PollComponent';
import useSetPollAnswer from 'hooks/modules/articles/useSetPollAnswer';
import { replaceAnchor } from 'presentation/utils/replaceAnchor';
import HTMLReactParser from 'html-react-parser';
import documentIcon from 'assets/img/icons/document.svg';
import { getFileSizeString } from 'presentation/utils/getFileSize';
import downloadFile from 'presentation/utils/downloadFile';
import { FaEye } from 'react-icons/fa';
interface Props {
  article: ArticleListItem;
  color: string;
  page: number;
}

const PrivateChannelArticleListItem: FC<Props> = ({ article, page }) => {
  const theme = useAppTheme();

  const { t } = useTranslation();

  const navigate = useNavigate();

  const setPollAnswer = useSetPollAnswer(article.id, (article.poll?.id ?? -1), page);

  const setReaction = useSetArticleReaction(
    article.id,
    article.selectedReactionId,
    page
  );
  const [contextMenu, setContextMenu] = useState({
    show: false,
    position: { x: 0, y: 0 },
  });

  const gallery = article.image ? [article.image, ...article.gallery] : article.gallery;

  const handleContextMenu = (event: {
    preventDefault: () => void;
    clientX: any;
    clientY: any;
  }): void => {
    event.preventDefault();
    setContextMenu({
      show: true,
      position: { x: event.clientX, y: event.clientY },
    });
  };

  const handleCloseContextMenu = (): void => {
    setContextMenu({ show: false, position: { x: 0, y: 0 } });
  };

  return (
    <Box
      onContextMenu={handleContextMenu}
      onClick={handleCloseContextMenu}
    >
      <AppCard overflow="hidden" p="0px" mr="10px">
        <Box h="full" w="full" mb="2px" blur="2xl" onClick={() =>
          navigate(
            AppRoute.privateChannelArticleComments(article.id, article.isCommentable)
          )}>
          {gallery != null && gallery.length !== 0 && (
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            <AspectRatio ratio={gallery[0].width! / gallery[0].height! }>
              <Image
                w="full"
                blur="2xl"
                src={gallery[0].url}
                objectFit="fill"
              />
            </AspectRatio>)}
        </Box>
        {article.video != null && (<Box
          h="full" w="full" mb="2px" blur="2xl"
          as="video"
          controls
          src={article.video.url}
          objectFit="contain"
        />)}
        <Box pb="20px" px="20px" pt="25px"
        >
          <Flex>
            <Text mb="2px" fontSize="20px" fontWeight={700}>
              {moment(article.publicDate).format('DD.MM.yyyy')}
            </Text>
            <Spacer />
            <Box display="flex" alignItems="center">
              <FaEye color="grey" width="25px" height="17px" />
              <Text ml="5px" fontSize={20} color="grey">
                {article.viewCount}
              </Text>
            </Box>
          </Flex>
          <Text mb="10px" fontSize="26px" fontWeight={800}>
            {article.name}
          </Text>
          <Text mb="16px" fontSize="20px">
            {HTMLReactParser(article.text ?? '', { replace: replaceAnchor })}
          </Text>
          {article.poll != null && (
            <Box mb="16px"><PollComponent poll={article.poll} selectedAnswer={(val) => {
              setPollAnswer(val);
            }} /></Box>)}
          {article.presentation != null && (
            <Flex mb="16px" gap="8px">
              <Image src={documentIcon} w="37px" h="37px"
                     onClick={() => downloadFile(article.presentation?.url ?? '')} />
              <Box alignItems="flex-end">
                <Text fontWeight="bold" fontSize={16}>{article.presentation.fileName}</Text>
                <Text fontWeight="normal" fontSize={12}>{getFileSizeString({
                  bytes: article.presentation.size ?? 0,
                  decimals: 2,
                })}</Text>
              </Box>
            </Flex>)}
          {article.tags && (
            <TagsRow
              tagColor={theme.colors[Color.Primary500]}
              tags={article.tags}
              mb="10px"
            />
          )}
          <ReactionsRow
            reactions={article.reactions}
            selectedReactionId={article.selectedReactionId}
            selectReaction={setReaction}
          />
        </Box>
        {article.isCommentable && (
          <Flex
            onClick={() =>
              navigate(
                AppRoute.privateChannelArticleComments(article.id, article.isCommentable)
              )
            }
            cursor="pointer"
            alignItems="center"
            pt="14px"
            px="20px"
            pb="16px"
            borderTop="1px solid #F5F5F5"
          >
            {article.commentsCount === 0 && <Image src={bubbleIcon} h="30px" />}
            {article.commentsCount > 0 && (
              <CommentAuthorsPreview
                authors={article.commentators.map(
                  (commentator) => commentator.photo.url
                )}
              />
            )}
            <Box w="10px" />
            <Text flex="1" fontSize="20px" color={Color.Primary500}>
              {article.commentsCount > 0
                ? getCountText(
                  [
                    t('commentariesCount1', { count: article.commentsCount }),
                    t('commentariesCount2', { count: article.commentsCount }),
                    t('commentariesCount3', { count: article.commentsCount }),
                    t('commentariesCount4', { count: article.commentsCount }),
                  ],
                  article.commentsCount
                )
                : t('leaveAComment')}
            </Text>
            <Box w="10px" />
            <Image src={chevronForward} h="20px" />
          </Flex>
        )}
      </AppCard>
      {contextMenu.show && (
        <Fade in={contextMenu.show}>
          <Box
            position="absolute"
            left={contextMenu.position.x - 20}
            top={contextMenu.position.y - 10}
            onMouseLeave={handleCloseContextMenu}
          >
            <Flex
              flexWrap="wrap"
              maxW="300px"
              bg="white"
              borderRadius="10px"
              rounded="md"
              shadow="lg"
            >
              {Object.keys(availableReactions).map((key) => (
                <Box
                  key={key}
                  py="10px"
                  cursor="pointer"
                  px="10px"
                  onClick={(e) => {
                    e.stopPropagation();
                    setReaction({
                      id: +key,
                      emoji: availableReactions[+key],
                    });
                    handleCloseContextMenu();
                  }}
                >
                  <Text fontSize="20px">{availableReactions[+key]}</Text>
                </Box>
              ))}
            </Flex>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default PrivateChannelArticleListItem;
